<template>
  <vue-modal name="splashPageModal" ref="splashPageMobileVueModal" :click-to-close="false" :show-close-icon="true"
    height="100%" width="100%" :classes="['splash-page-modal', 'v--modal']" :style="modalStyles" :scrollable="false"
    @before-open="beforeOpen" @opened="opened" @before-close="beforeClose" @closed="closed">
    <section class="splash-page-modal-wrapper">
      <base-spinner v-show="isLoading" classes="spinner" position="absolute">
      </base-spinner>
      <component :is="splashPageComponent" :ref="splashPageComponent" :is-touch-started="istouchStarted"
        :is-loading="isLoading" @restoreModel3D="restoreModel3D">
        <template>
          <div v-if="!editorVisible" class="model-3d" id="model3d"></div>
        </template>
      </component>
    </section>
  </vue-modal>
</template>

<script>
import simplifiedSplashMobile from '@/components/SplashPage/SimplifiedSplashMobile';
import simplifiedSplashDesktop from '@/components/SplashPage/SimplifiedSplashDesktop';
import sceneManager from '@/mixins/Customizer/SceneManager';
import baseSpinner from '@/components/Spinner';
import geometryUpdate from '@/mixins/geometryUpdates';
import swatchMaterial from '@/mixins/Customizer/SwatchMaterial';
import GraphicEditorDesktop from '@/components/SplashPage/GraphicEditorDesktop/GraphicEditorDesktop';
import GraphicEditorMobile from '@/components/SplashPage/GraphicEditorMobile/GraphicEditorMobile';

import { sessionKeys } from '@/data/constants';
const TWEEN = require('@tweenjs/tween.js');
const MODEL_3D_ID_SELECTOR = '#model3d';

export default {
  name: "splashPageModal",
  mixins: [sceneManager, geometryUpdate, swatchMaterial],
  components: {
    baseSpinner,
    simplifiedSplashMobile,
    simplifiedSplashDesktop,
    GraphicEditorDesktop,
    GraphicEditorMobile
  },
  data() {
    return {
      recipeTemp: {},
      activeComponentTemp: null,
      isLoading: true,
      istouchStarted: false,
      modalStyles: {
        minHeight: "auto",
      },
      bodyStylePosition: null,
      splashPageComponent: 'simplifiedSplashDesktop'
    }
  },
  computed: {
    editorVisible() {
      return this.$store.state.graphicsController.editorVisible;
    },
    showPreloader() {
      return this.$store.state.preloader.showPreloader;
    },
    isBackpack() {
      return this.$store.state.utils.pdm !== null && this.$store.state.utils.pdm.sku === 'backpack';
    },
    isMobile() {
      return this.$store.getters["utils/returnScreenSize"].includes('mobile');
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    isEnableCollapsableHeader() {
      return this.$store.state.utils.configuration.enableCollapsableHeader;
    },
    queryParams() {
      return this.$store.state.utils.queryParams;
    },
  },
  watch: {
    showPreloader: {
      immediate: true,
      handler: 'checkShowModal',
    },
    isLoading(newValue) {
      this.$eventBus.$emit("hide-customs-spinner", !newValue);
    }
  },
  methods: {
    /**
     * @function beforeOpen
     * Saves the current recipe to apply it once the modal is closed.
     */
    beforeOpen() {
      if (this.isEnableCollapsableHeader && !this.isMobile) {
        this.$eventBus.$emit('hideCollapsingHeader');
      }
      this.isLoading = true;
      this.recipeTemp = JSON.parse(JSON.stringify(this.$store.state.recipe.recipe_v2));
      this.activeComponentTemp = this.$store.state.legacyNavigation.activeComponent;
    },
    /**
     * @function checkRotateStart
     * Set the touch flag to true
     */
    checkRotateStart() {
      this.istouchStarted = true;
    },
    /**
     * @function opened
     * Apply the default recipe and append the main scene inside
     * splash div model3d
     */
    opened() {
      this.initSplashPage();
      this.$nextTick(() => {
        const canvasContainer = document.querySelector(MODEL_3D_ID_SELECTOR);
        if (!this.editorVisible) {
          this.adjustModalHeight();
          if (canvasContainer) {
            canvasContainer.append(document.querySelector("canvas"));
          }
          if (this.$refs.simplifiedSplashPageDesktop) this.$refs.simplifiedSplashDesktop.setModel3DContainerEl(canvasContainer);
          this.rotate3dModel();
          this.$eventBus.$emit("change-scene-settings", false);
        }
      });
      this.$nextTick(() => this.isLoading = false);
    },
    /**
     * @function checkShowModal
     * This method is called when showPreloader changes and, if the modal
     * should show, it calls the method to show the Splash Page Modal.
     */
    checkShowModal(showPreloader) {
      if (!showPreloader) this.$nextTick(() => {
        if (this.queryParams.edit || (this.queryParams.recipe && this.queryParams.recipe !== this.$store.state.recipe.recipeId)) {
          return
        }
        this.show()
      }
      );
    },
    /**
     * @function show
     * This method opens the Splash Page Modal onLoad.
     */
    show() {
      const { ugcImage } = this.$store.state.utils.queryParams;
      const { tempData } = this.$store.state.ugc;
      if (!this.$cookies.get(sessionKeys.splashPageShown) && !ugcImage && !tempData.features[tempData.activeUGCIndex]
.ugcFeature) {
        this.$cookies.set(sessionKeys.splashPageShown, true, 0);
        this.$modal.show('splashPageModal');
        this.$store.commit('utils/SET_IS_SPLASH_MODAL_OPEN', true);
      }
    },
    /**
     * @function restoreModel3D
     * This method applies the recipe and closes the Splash Page Modal.
     */
    restoreModel3D() {
      this.$store.commit('utils/SET_IS_SPLASH_MODAL_OPEN', false);
      if (this.editorVisible) {
        this.exit();
        return;
      }
      this.$eventBus.$emit("change-scene-settings");
      this.isLoading = true;
      document.querySelector(MODEL_3D_ID_SELECTOR).removeEventListener('touchstart', this.checkRotateStart);
      new Promise((resolve, reject) => {
        document.querySelector("#scene").append(document.querySelector("canvas"));
        resolve();
      })
        .then(this.loadNew3DAssets)
        .then(() => {
          this.exit();
        });
    },
    /**
     * @function exit
     * This functions close the Splash Page Modal.
     */
    exit() {
      this.isLoading = false;
      this.$modal.hide('splashPageModal');
      if (this.isEnableCollapsableHeader && !this.isMobile) {
        this.$eventBus.$emit('rollbackCollapsingHeader');
      }
    },
    /**
     * @function rotate3dModel
     * This function will animate the 3d model when load the splash page
     */
    rotate3dModel() {
      const model3D = document.querySelector(MODEL_3D_ID_SELECTOR);
      if (model3D) {
        model3D.addEventListener('touchstart', this.checkRotateStart);
        this.$model3D.rootObj.rotation.set(this.$model3D.rootObj.rotation.x, 0, this.$model3D.rootObj.rotation.z);
        new TWEEN.default.Tween(this.$model3D.rootObj.rotation)
          .to({ y: Math.PI * 2 }, 2500)
          .delay(400)
          .easing(TWEEN.default.Easing.Back.InOut)
          .start();
      }
    },
    onBackgroundClick() {
      if (!this.isMobile && this.$refs.simplifiedSplashDesktop) this.$refs.simplifiedSplashDesktop.closeClick();
      if (this.isMobile && this.$refs.splashPageModalMobile) this.$refs.splashPageModalMobile.onSkip();
    },
    beforeClose() {
      document.body.style.overflow = '';
      this.$refs.splashPageMobileVueModal.$el.removeEventListener('click', this.onBackgroundClick);
    },
    adjustModalHeight() {
      const height = window.innerHeight;
      this.modalStyles = {
        ...this.modalStyles,
        height: `${height}px`,
      };
    },
    closed() {
      this.$customizerDataLayer.logAnalyticsEvent({
        type: this.$customizerDataLayer.types.customizerSplashPage,
        eventLabel: 'Close Modal',
        forceEcomDataLayerFormat: true,
      });
    },
    initSplashPage() {
      const enableGraphicEditorSplashPage = this.$store.state.utils.configuration.splash_page
        .enableGraphicEditorSplashPage;
      const showGraphicEditorSplashPage = (enableGraphicEditorSplashPage && this.queryParams.edit && this.editorVisible) || (enableGraphicEditorSplashPage && this.editorVisible);
      document.body.style.overflow = 'hidden';
      if (!this.isMobile) {
        if (showGraphicEditorSplashPage) {
          this.$store.commit("graphicsController/SET_EDITOR_VISIBLE",true);
          this.splashPageComponent = 'GraphicEditorDesktop';
          this.modalStyles['position'] = 'absolute';
        } else {
          this.$store.commit("graphicsController/SET_EDITOR_VISIBLE",false);
          this.splashPageComponent = 'simplifiedSplashDesktop';
        }
      } else {
        if (showGraphicEditorSplashPage) {
          this.$store.commit("graphicsController/SET_EDITOR_VISIBLE",true);
          this.splashPageComponent = 'GraphicEditorMobile';
        }else {
          this.$store.commit("graphicsController/SET_EDITOR_VISIBLE",false);
          this.splashPageComponent = 'simplifiedSplashMobile';
        }
      }
    },
  },
  created() {
    this.$eventBus.$on("splash-page-background-click", this.onBackgroundClick);
    this.$eventBus.$on("splash-page-graphic-editor-event", this.show);
    window.addEventListener('resize', this.adjustModalHeight);
  },
  beforeDestroy() {
    this.$eventBus.$off("splash-page-background-click", this.onBackgroundClick);
    this.$eventBus.$off("splash-page-graphic-editor-event", this.show);
    window.removeEventListener('resize', this.adjustModalHeight);
  },
}
</script>
